import { Component, input, output, OnInit, inject, effect, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { NumberFormatDirective } from 'app/core/directives/number-format.directive';
import { FormMode } from 'app/core/constants/form-mode';
import { CatalogService } from '@services/data/catalog.service';
import { RuleService } from 'app/feature/management/rule-management/services/rule.service';
import { AppSettings } from '@services/settings/app-settings.service';
import Swal from 'sweetalert2';

const SENSITIVE_CONTROLS = ['year', 'value', 'vin', 'brandCode', 'modelCode'] as const;

@Component({
  selector: 'app-vehicle-form',
  templateUrl: './vehicle-form.component.html',
  styleUrl: './vehicle-form.component.css',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    NumberFormatDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: []
})
export class VehicleFormComponent implements OnInit {
  vehicleForm = input.required<FormGroup>();
  formChange = output<FormGroup>();
  isReadOnly = input(false);
  mode = input(FormMode.CREATE);
  /** quick: sensibles disabled; full/default: editables según mode/isReadOnly */
  editMode = input<'default' | 'quick' | 'full'>('default');
  FormMode = FormMode;
  readonly appSettings = inject(AppSettings);
  public brands: any;
  public models: any;
  modelsTemp: any;
  public vehicleTypes: any;
  public statuses: any;

  brandsList = input<any[] | null>(null);
  modelsList = input<any[] | null>(null);

  private catalogService = inject(CatalogService);
  private ruleService = inject(RuleService);
  public maxVehicleAge: number | null = null;
  public minYear: number | null = null;

  constructor() {
    effect(() => {
      this.brands = this.brandsList() ?? this.catalogService.brands() ?? [];
      this.statuses = this.catalogService.statuses() ?? [];
      const sourceModels = this.modelsList() ?? this.catalogService.models() ?? [];
      if (sourceModels.length > 0) {
        this.modelsTemp = JSON.parse(JSON.stringify(sourceModels));
        this.filterModels();
      }
    });

    effect(() => {
      const form = this.vehicleForm();
      const editMode = this.editMode();
      const readOnly = this.isReadOnly();
      if (!form) return;
      for (const key of SENSITIVE_CONTROLS) {
        const ctrl = form.get(key);
        if (!ctrl) continue;
        if (readOnly || editMode === 'quick') {
          ctrl.disable({ emitEvent: false });
        } else {
          ctrl.enable({ emitEvent: false });
        }
      }
    });
  }

  ngOnInit(): void {
    this.ruleService.get().subscribe({
      next: (rules: any[]) => this.setupAgeRule(rules),
      error: (err) => console.error('Error al cargar config de Regla VEHICLE_MAX_AGE', err)
    });
  }

  // Configura regla de antigüedad máxima
  private setupAgeRule(rules: any[]) {
    const ageRule = rules.find(r => r.slug === 'VEHICLE_MAX_AGE' && r.isActive);
    if (!ageRule?.value) return;
    this.maxVehicleAge = parseInt(ageRule.value, 10);
    this.minYear = new Date().getFullYear() - this.maxVehicleAge;
  }

  // Valida antigüedad máxima permitida según regla
  private isVehicleAgeValid(year: number): boolean {
    if (!year || this.maxVehicleAge === null) return true;
    const minYear = new Date().getFullYear() - this.maxVehicleAge;
    if (year >= minYear) return true;
    this.showAgeAlert(minYear);
    return false;
  }

  // Notifica alerta de antigüedad excedida
  private showAgeAlert(minYear: number) {
    Swal.fire({
      icon: 'warning',
      title: 'Antigüedad Excedida',
      text: `El vehículo excede la antigüedad permitida de ${this.maxVehicleAge} años. Año mínimo: ${minYear}.`,
      confirmButtonText: 'Entendido'
    });
  }

  submit() {
    if (!this.vehicleForm()?.valid) return;
    const year = this.vehicleForm().get('year')?.value;
    if (!this.isVehicleAgeValid(year)) return;
    this.formChange.emit(this.vehicleForm());
  }

  onBrandChange() {
    this.vehicleForm().get('modelCode')?.reset();
    this.filterModels();
  }

  onYearChange() {
    this.vehicleForm().get('modelCode')?.reset();
    this.filterModels();
  }

  filterModels() {
    if (!this.modelsTemp) return;
    const brandCode = this.vehicleForm().get('brandCode')?.value;
    const year = this.vehicleForm().get('year')?.value;
    this.models = this.modelsTemp.filter((model: any) => {
      const matchesBrand = brandCode ? model.brandCode === brandCode : true;
      const matchesYear = year ? model.year === year : true;
      return matchesBrand && matchesYear;
    });
  }

  onModelChange() {
    const modelCode = this.vehicleForm().get('modelCode')?.value;
    if (modelCode && this.modelsTemp) {
      const selectedModel = this.modelsTemp.find((m: any) => m.code === modelCode);
      if (selectedModel) {
        this.vehicleForm().patchValue({ vehicleType: selectedModel.vehicleType });
      }
    }
  }

  displayValue(value: any): string {
    const text = value == null ? '' : String(value).trim();
    return text || '—';
  }

  findBrand(code: string): string {
    const brand = this.brands?.find((b: any) => b.code === code);
    return brand ? brand.name : code;
  }

  findModel(code: string): string {
    const model = (this.modelsTemp || this.models)?.find((m: any) => m.code === code);
    return model ? model.name : code;
  }
}
